<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/GeneralCodes">General Codes</b-breadcrumb-item>
            <b-breadcrumb-item active>Add</b-breadcrumb-item>
        </b-breadcrumb>

        <form @submit.prevent="onSubmit()">
            <div class="card">
                <div class="bg-dark card-header text-white">Create New General Code</div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-6 col-sm-12 form-group">
                            <label for="Entity">Entity</label>
                            <AppTextBoxAutoComplete :classes="{'is-invalid': $v.generalCode.entity.$invalid}"
                                                    :is-async="true"
                                                    :items="generalCodeEntities"
                                                    @input="[onEntitySearch($event),debouncedSearch($event)]"
                                                    @selected="onEntitySelected()"
                                                    maxlength="20"
                                                    placeholder="Search Entity Name..."
                                                    v-model="searchedEntity.name"></AppTextBoxAutoComplete>
                            <span class="invalid-feedback"
                                  v-if="!$v.generalCode.entity.required">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.generalCode.entity.maxLength">Entity name must be &le; 20 characters</span>
                        </div>
                        <div class="col-md-6 col-sm-12 form-group">
                            <label for="EntityShortDescription">Entity Short Description</label>
                            <input :class="{'is-invalid': $v.generalCode.entityShortDescription.$invalid}"
                                   :disabled="isEntitySelected" autocomplete="off"
                                   class="form-control"
                                   maxlength="100" placeholder="Entity Short Description"
                                   v-model="generalCode.entityShortDescription"/>
                            <span class="invalid-feedback" v-if="!$v.generalCode.entityShortDescription.maxLength">Entity short description must be &le; 100 characters</span>
                        </div>
                        <div class="col-md-6 col-sm-12 form-group">
                            <label for="Code">Code</label>
                            <input :class="{'is-invalid': $v.generalCode.code.$invalid}" autocomplete="off"
                                   class="form-control" maxlength="15"
                                   placeholder="Code"
                                   v-model="generalCode.code"/>
                            <span class="invalid-feedback" v-if="!$v.generalCode.code.maxLength">Code must be &le; 15 characters</span>
                        </div>
                        <div class="col-md-6 col-sm-12 form-group">
                            <label for="GroupingCode">Grouping Code</label>
                            <input :class="{'is-invalid': $v.generalCode.groupingCode.$invalid}" autocomplete="off"
                                   class="form-control"
                                   maxlength="10"
                                   placeholder="Grouping Code" v-model="generalCode.groupingCode"/>
                            <span class="invalid-feedback" v-if="!$v.generalCode.groupingCode.maxLength">Grouping code must be &le; 10 characters</span>
                        </div>
                        <div class="col-sm-12 form-group">
                            <label for="ShortDescription">Short Description</label>
                            <input :class="{'is-invalid': $v.generalCode.shortDescription.$invalid}" autocomplete="off"
                                   class="form-control"
                                   maxlength="255"
                                   placeholder="Short Description" v-model="generalCode.shortDescription"/>
                            <span class="invalid-feedback"
                                  v-if="!$v.generalCode.shortDescription.required">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.generalCode.shortDescription.maxLength">Short description must be &le; 255 characters</span>
                        </div>
                        <div class="col-sm-12 form-group">
                            <label for="LongDescription">Long Description</label>
                            <textarea :class="{'is-invalid': $v.generalCode.longDescription.$invalid}"
                                      autocomplete="off"
                                      class="form-control"
                                      maxlength="2000"
                                      placeholder="Long Description" v-model="generalCode.longDescription"></textarea>
                            <span class="invalid-feedback" v-if="!$v.generalCode.longDescription.maxLength">Long description must be &le; 2000 characters</span>
                        </div>
                    </div>
                </div>
                <div class="card-footer d-print-none text-right">
                    <button :disabled="isSubmitting" class="btn btn-primary" type="submit">Save</button>
                    <RouterLink :to="{name:'GeneralCodes'}" class="btn btn-danger">Back</RouterLink>
                </div>
            </div>
        </form>

    </div>
</template>

<script>
    import AppTextBoxAutoComplete from "@/components/shared/AppTextBoxAutoComplete";
    import {maxLength, required} from "vuelidate/lib/validators";
    import GeneralCodeService from "@/services/GeneralCodeService";
    import _ from 'lodash'
    import {ToastType} from "@/services/models/shared/ToastType";

    export default {
        components: {
            AppTextBoxAutoComplete
        },
        data() {
            return {
                isSubmitting: false,
                generalCode: {
                    entity: null,
                    entityShortDescription: null,
                    shortDescription: null,
                    longDescription: null,
                    code: null,
                    groupingCode: null
                },
                generalCodeEntities: [],
                searchedEntity: {
                    name: null,
                    description: null
                },
                isEntitySelected: false,

                // The function for the type ahead search, which only get executed once a second max
                debouncedSearch: _.debounce((value) => {
                    const vm = this;

                    // Remove the existing item collection
                    vm.generalCodeEntities.splice(0, vm.generalCodeEntities.length);

                    // Replace the existing item collection based on the filtered value
                    GeneralCodeService.getEntities({
                        pageSize: 10,
                        page: 1,
                        name: value
                    }).then(response => {
                        vm.generalCodeEntities = response.data;
                    })
                }, 1000, {
                    leading: false, trailing: true
                })
            }
        },
        methods: {
            /**
             * @description Updates the general code entity to the searched value (we may not want to select one from the list)
             */
            onEntitySearch(value) {
                this.generalCode.entity = value;
                this.isEntitySelected = false;
            },

            /**
             * @description If an entity is selected from the type ahead search, updates all appropriate models
             */
            onEntitySelected(value) {
                this.isEntitySelected = true;

                // Assign the id of the name based on the selected object
                this.generalCode.entityShortDescription = value.description;
                this.generalCode.entity = value.name;
                this.searchedEntity.name = value.name;
            },

            /**
             * @description Creates a new general code and redirects if successful
             */
            onSubmit() {
                const vm = this;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                GeneralCodeService.addGeneralCode(vm.generalCode).then(() => {
                    vm.alert(ToastType.Success, `The general code '${vm.generalCode.code} was successfully created.`);

                    // Redirect to the general codes page
                    vm.$router.replace({name: 'GeneralCodes'})
                }).catch(function (error) {
                    vm.alert(ToastType.Danger, error.response.statusText)
                }).finally(() => {
                    vm.isSubmitting = false;
                });
            }
        },
        validations: {
            generalCode: {
                shortDescription: {
                    required,
                    maxLength: maxLength(255)
                },
                entity: {
                    required,
                    maxLength: maxLength(20)
                },
                entityShortDescription: {
                    maxLength: maxLength(100)
                },
                longDescription: {
                    maxLength: maxLength(2000)
                },
                groupingCode: {
                    maxLength: maxLength(10)
                },
                code: {
                    maxLength: maxLength(15)
                }
            }
        }
    }
</script>
